import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Check,
  Clock,
  History,
  Info,
  Loader2,
  Megaphone,
  Music2,
  Package,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShoppingBag,
  Trash2,
  Unplug,
  Film,
  Video,
  X,
} from "lucide-react";
import AppShell from "../components/AppShell";
import AccountLabel, { AccountIdBadge } from "../components/AccountLabel";
import HistoryDrawer from "../components/HistoryDrawer";
import { getAccountDisplayTitle } from "../shared/utils/account-display";
import { groupMetaConnections } from "../shared/utils/meta-business-groups";
import ApiService from "../services/ApiService";
import IntegrationsService from "../services/IntegrationsService";
import InsightsService from "../services/InsightsService";
import { cn } from "../components/ui/cn";

const SYNC_INTERVALS = [
  { value: "manual", label: "Manual only" },
  { value: "daily", label: "Daily · 2 AM UTC" },
  { value: "hourly", label: "Every hour" },
];

const DATE_WINDOWS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
];

const METRIC_PRESETS = [
  { value: "core", label: "Core metrics" },
  { value: "all", label: "All available metrics" },
];

const OAUTH_START = {
  meta: () => IntegrationsService.startMetaOAuth(),
  google: () => IntegrationsService.startGoogleOAuth(),
  search_console: () => IntegrationsService.startSearchConsoleOAuth(),
  google_ads: () => IntegrationsService.startGoogleAdsOAuth(),
  linkedin: () => IntegrationsService.startLinkedInOAuth(),
  tiktok: () => IntegrationsService.startTikTokOAuth(),
  youtube: () => IntegrationsService.startYouTubeOAuth(),
};

const PROVIDER_META = {
  meta: { color: "#1877F2", bg: "bg-blue-50", ring: "ring-blue-100", label: "Meta", icon: Megaphone },
  google: { color: "#EA4335", bg: "bg-red-50", ring: "ring-red-100", label: "GA4", icon: BarChart3 },
  search_console: { color: "#34A853", bg: "bg-emerald-50", ring: "ring-emerald-100", label: "Search Console", icon: Search },
  google_ads: { color: "#FBBC04", bg: "bg-amber-50", ring: "ring-amber-100", label: "Google Ads", icon: Megaphone },
  linkedin: { color: "#0A66C2", bg: "bg-blue-50", ring: "ring-blue-100", label: "LinkedIn", icon: Megaphone },
  tiktok: { color: "#010101", bg: "bg-slate-100", ring: "ring-slate-200", label: "TikTok", icon: Music2 },
  shopify: { color: "#96BF48", bg: "bg-lime-50", ring: "ring-lime-100", label: "Shopify", icon: ShoppingBag },
  youtube: { color: "#FF0000", bg: "bg-red-50", ring: "ring-red-100", label: "YouTube", icon: Film },
  youtube_channel: { color: "#FF0000", bg: "bg-red-50", ring: "ring-red-100", label: "YouTube", icon: Film },
  youtube_ads: { color: "#FF0000", bg: "bg-red-50", ring: "ring-red-100", label: "YouTube Ads", icon: Video },
};

/** Hub filter tabs — Overview shows all connections. */
const PLATFORM_TABS = [
  { id: "all", label: "Overview", providers: null },
  { id: "meta", label: "Facebook + Instagram", providers: ["meta"] },
  { id: "google", label: "GA4", providers: ["google"] },
  { id: "search_console", label: "Search Console", providers: ["search_console"] },
  { id: "google_ads", label: "Google Ads", providers: ["google_ads"] },
  { id: "linkedin", label: "LinkedIn Ads", providers: ["linkedin"] },
  { id: "tiktok", label: "TikTok Ads", providers: ["tiktok"] },
  { id: "shopify", label: "Shopify", providers: ["shopify"] },
  { id: "youtube", label: "YouTube channel", providers: ["youtube", "youtube_channel"] },
  { id: "youtube_ads", label: "YouTube Ads", providers: ["youtube_ads"] },
];

function fmtDateTime(value) {
  if (!value) return "Never";
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function fmtDuration(started, finished) {
  if (!started || !finished) return null;
  const ms = new Date(finished) - new Date(started);
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function kindLabel(kind) {
  const labels = {
    instagram_business: "Instagram Business",
    facebook_page: "Facebook Page",
    facebook_ads_account: "Meta Ads",
    facebook_catalog: "Meta Catalog",
    ga4_property: "GA4 Property",
    search_console_site: "Search Console",
    google_ads_customer: "Google Ads",
    linkedin_ads_account: "LinkedIn Ads",
    tiktok_ads_account: "TikTok Ads",
    shopify_store: "Shopify Store",
    youtube_channel: "YouTube channel",
    youtube_ads: "YouTube Ads",
  };
  return labels[kind] || kind;
}

function StatusBadge({ row }) {
  if (row?.syncPaused) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        Paused
      </span>
    );
  }
  if (row?.status === "needs_reconnect") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        Needs reconnect
      </span>
    );
  }
  if (row?.status === "error") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
        Error
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Active
    </span>
  );
}

function ProviderBrandIcon({ provider, className }) {
  const meta = PROVIDER_META[provider] || PROVIDER_META.meta;
  const Icon = meta.icon;
  return (
    <div
      className={cn(
        "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1",
        meta.bg,
        meta.ring,
        className
      )}
    >
      <Icon className="h-5 w-5" style={{ color: meta.color }} strokeWidth={2} />
    </div>
  );
}

function Toast({ snackbar, onClose }) {
  if (!snackbar) return null;
  const styles = {
    success: "bg-emerald-600",
    warning: "bg-amber-500",
    error: "bg-red-600",
  };
  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div
        className={cn(
          "flex max-w-sm items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium text-white shadow-lg",
          styles[snackbar.severity] || styles.success
        )}
      >
        <span className="flex-1">{snackbar.message}</span>
        <button type="button" onClick={onClose} className="rounded p-0.5 hover:bg-white/20">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function PlatformCard({
  provider,
  connectedCount,
  isConnecting,
  onConnect,
  onSyncPlatform,
  syncingPlatform,
  onDisconnectAll,
  disconnectingPlatform,
  selected,
  onSelect,
}) {
  const disabled = !provider.configured || provider.comingSoon;
  const meta = PROVIDER_META[provider.provider] || PROVIDER_META.meta;
  let statusLabel = "Ready to connect";
  let statusClass = "text-slate-500 bg-slate-50 border-slate-200";
  if (provider.comingSoon) {
    statusLabel = "Coming soon";
    statusClass = "text-slate-400 bg-slate-50 border-slate-200";
  } else if (!provider.configured) {
    statusLabel = "Not configured";
    statusClass = "text-amber-700 bg-amber-50 border-amber-200";
  } else if (connectedCount > 0) {
    statusLabel = `${connectedCount} connected`;
    statusClass = "text-emerald-700 bg-emerald-50 border-emerald-200";
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(provider.provider)}
      className={cn(
        "analytics-card flex h-full w-full flex-col p-5 text-left transition-shadow hover:shadow-card-hover",
        selected && "ring-2 ring-indigo-500 ring-offset-2"
      )}
    >
      <div
        className="mb-4 h-1 w-full rounded-full"
        style={{
          background: disabled
            ? "#E2E8F0"
            : `linear-gradient(90deg, ${meta.color}, ${meta.color}55)`,
        }}
      />
      <div className="flex items-start gap-3">
        <ProviderBrandIcon provider={provider.provider} />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-900">{provider.label}</h3>
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">
            {provider.description}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-1 flex-col justify-end gap-2">
        <span
          className={cn(
            "inline-flex w-fit items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
            statusClass
          )}
        >
          {statusLabel}
        </span>
        <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
          {!disabled && (
            <button
              type="button"
              disabled={isConnecting}
              onClick={() => onConnect(provider.provider)}
              className={cn(
                "inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition",
                connectedCount > 0
                  ? "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  : "bg-indigo-600 text-white hover:bg-indigo-700",
                isConnecting && "opacity-50"
              )}
            >
              {isConnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : connectedCount > 0 ? <Plus className="h-3.5 w-3.5" /> : null}
              {isConnecting ? "Opening…" : connectedCount > 0 ? "Add" : "Connect"}
            </button>
          )}
          {connectedCount > 0 && !disabled && (
            <button
              type="button"
              disabled={syncingPlatform === provider.provider}
              onClick={() => onSyncPlatform(provider.provider)}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {syncingPlatform === provider.provider ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Sync
            </button>
          )}
          {connectedCount > 0 && !disabled && (
            <button
              type="button"
              disabled={disconnectingPlatform === provider.provider}
              onClick={() => onDisconnectAll(provider)}
              className="inline-flex basis-full items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              {disconnectingPlatform === provider.provider ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Unplug className="h-3.5 w-3.5" />
              )}
              Disconnect all
            </button>
          )}
          {disabled && (
            <span className="text-xs font-medium text-slate-400">
              {provider.comingSoon ? "App Review / setup pending" : "Orka app not configured"}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function SettingsDrawer({ row, open, onClose, onSave, saving }) {
  const [displayName, setDisplayName] = useState("");
  const [syncInterval, setSyncInterval] = useState("daily");
  const [defaultWindow, setDefaultWindow] = useState("30d");
  const [metricsPreset, setMetricsPreset] = useState("core");
  const [syncPaused, setSyncPaused] = useState(false);

  useEffect(() => {
    if (!row) return;
    setDisplayName(row.displayName || "");
    setSyncInterval(row.syncInterval || "daily");
    setDefaultWindow(row.defaultWindow || "30d");
    setMetricsPreset(row.metricsPreset || "core");
    setSyncPaused(Boolean(row.syncPaused));
  }, [row]);

  if (!open || !row) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Edit settings</h3>
            <p className="text-xs text-slate-500">{kindLabel(row.providerAccountKind)}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Display name</span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Sync schedule</span>
            <select
              value={syncInterval}
              onChange={(e) => setSyncInterval(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 shadow-sm"
            >
              {SYNC_INTERVALS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Default date range</span>
            <select
              value={defaultWindow}
              onChange={(e) => setDefaultWindow(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 shadow-sm"
            >
              {DATE_WINDOWS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Metrics preset</span>
            <select
              value={metricsPreset}
              onChange={(e) => setMetricsPreset(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 shadow-sm"
            >
              {METRIC_PRESETS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {/* TODO: wire metricsPreset into ingest when per-source metric filters ship */}
            <p className="mt-1 text-xs text-slate-400">Stored for this connection; sync still pulls core metrics today.</p>
          </label>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {syncPaused ? "Sync paused" : "Sync active"}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">
                  Pause stops pulls and hides this source from Report builder by default. OAuth tokens stay connected. Disconnect removes tokens.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSyncPaused((v) => !v)}
                className={cn(
                  "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold",
                  syncPaused
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
                )}
              >
                {syncPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                {syncPaused ? "Resume sync" : "Pause sync"}
              </button>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => onSave({
              displayName,
              syncInterval,
              defaultWindow,
              metricsPreset,
              syncPaused,
            })}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function SyncLogTable({ logs, loading }) {
  if (loading) {
    return (
      <div className="animate-pulse space-y-3 p-6">
        {[0, 1, 2].map((i) => <div key={i} className="h-12 rounded-lg bg-slate-100" />)}
      </div>
    );
  }
  if (!logs?.length) {
    return (
      <div className="px-6 py-12 text-center">
        <Clock className="mx-auto h-8 w-8 text-slate-300" />
        <p className="mt-3 text-sm text-slate-500">No sync runs yet.</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-left">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50/80">
            {["Status", "Started", "Trigger", "Accounts", "Success", "Failed", "Duration"].map((h) => (
              <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {logs.map((row) => {
            const failed = Number(row.failedCount || 0) > 0;
            return (
              <tr key={row.id} className="hover:bg-slate-50/60">
                <td className="px-4 py-3">
                  <span className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold",
                    failed ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"
                  )}>
                    {failed ? <X className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                    {failed ? "Failed" : "Success"}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-slate-800">{fmtDateTime(row.startedAt)}</td>
                <td className="px-4 py-3 text-xs font-semibold capitalize text-slate-600">{row.trigger}</td>
                <td className="px-4 py-3 text-sm tabular-nums">{row.totalAccounts}</td>
                <td className="px-4 py-3 text-sm font-semibold text-emerald-600">{row.successCount}</td>
                <td className="px-4 py-3 text-sm font-semibold text-red-600">{row.failedCount}</td>
                <td className="px-4 py-3 text-sm text-slate-500">{fmtDuration(row.startedAt, row.finishedAt) || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function IntegrationsView() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [providers, setProviders] = useState([]);
  const [connected, setConnected] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platformFilter, setPlatformFilter] = useState("all");
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingPlatform, setSyncingPlatform] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(null);
  const [deleteStoredData, setDeleteStoredData] = useState(false);
  const [bulkAck, setBulkAck] = useState(false);
  const [disconnectingPlatform, setDisconnectingPlatform] = useState(null);
  const [historyAccount, setHistoryAccount] = useState(null);
  const [settingsRow, setSettingsRow] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [discoveringCatalogs, setDiscoveringCatalogs] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [providersList, connectedList, me] = await Promise.all([
        IntegrationsService.listProviders(),
        IntegrationsService.listConnected({ includePaused: true }),
        ApiService.getAccountMe().catch(() => ({ account: null })),
      ]);
      setProviders(Array.isArray(providersList) ? providersList : []);
      setConnected(Array.isArray(connectedList) ? connectedList : []);
      setAccount(me?.account ?? null);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/signin");
        return;
      }
      setError(e?.response?.data?.message || e.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const refreshLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const data = await IntegrationsService.getSyncLogs(20);
      setSyncLogs(Array.isArray(data) ? data : []);
    } catch {
      setSyncLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    refreshLogs();
  }, [refresh, refreshLogs]);

  useEffect(() => {
    if (!snackbar) return undefined;
    const t = setTimeout(() => setSnackbar(null), 4000);
    return () => clearTimeout(t);
  }, [snackbar]);

  useEffect(() => {
    function onMessage(event) {
      const data = event?.data;
      if (!data) return;
      if (data.type === "meta-connected") {
        setConnectingProvider(null);
        setSnackbar({ severity: "success", message: "Meta connected." });
        refresh();
        return;
      }
      if (data.type !== "sm:oauth") return;
      const payload = data.payload || {};
      if (payload.ok && payload.provider === "meta") {
        setConnectingProvider(null);
        return;
      }
      setConnectingProvider(null);
      if (payload.ok) {
        setSnackbar({
          severity: "success",
          message: `Connected ${payload.count || 0} ${payload.provider || ""} account(s).`,
        });
        refresh();
      } else {
        setSnackbar({ severity: "error", message: payload.error || "Connection failed." });
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [refresh]);

  const overview = useMemo(() => {
    const total = connected.length;
    const errors = connected.filter((r) => (r.status === "error" || r.status === "needs_reconnect") && !r.syncPaused).length;
    const paused = connected.filter((r) => r.syncPaused).length;
    const lastSync = connected
      .map((r) => r.lastSyncedAt)
      .filter(Boolean)
      .sort((a, b) => new Date(b) - new Date(a))[0] || null;
    return { total, errors, paused, lastSync };
  }, [connected]);

  const filteredConnected = useMemo(() => {
    const tab = PLATFORM_TABS.find((t) => t.id === platformFilter);
    if (!tab || !tab.providers) return connected;
    return connected.filter((c) => tab.providers.includes(c.provider));
  }, [connected, platformFilter]);

  const metaGroups = useMemo(
    () => (platformFilter === "meta" ? groupMetaConnections(filteredConnected) : null),
    [filteredConnected, platformFilter],
  );

  function openOAuthPopup(url) {
    const w = 620;
    const h = 740;
    const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
    const popup = window.open(url, "sm-oauth", `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) {
      setConnectingProvider(null);
      setSnackbar({ severity: "warning", message: "Popup blocked. Allow popups and try again." });
    }
  }

  async function handleConnect(provider) {
    const startFn = OAUTH_START[provider];
    if (!startFn) {
      setSnackbar({ severity: "warning", message: "This connection is coming soon." });
      return;
    }
    setConnectingProvider(provider);
    try {
      const { url } = await startFn();
      openOAuthPopup(url);
    } catch (e) {
      setConnectingProvider(null);
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Failed to start OAuth." });
    }
  }

  async function handleRefreshOne(id) {
    setBusyId(id);
    try {
      await InsightsService.refreshOne(id);
      setSnackbar({ severity: "success", message: "Synced successfully." });
      await Promise.all([refresh(), refreshLogs()]);
    } catch (e) {
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Sync failed." });
      await refresh();
    } finally {
      setBusyId(null);
    }
  }

  async function handleSyncAll() {
    setSyncingAll(true);
    try {
      const results = await InsightsService.refreshAll();
      const list = Array.isArray(results) ? results : [];
      const ok = list.filter((r) => r.ok).length;
      const skipped = list.filter((r) => r.skipped).length;
      const failed = list.filter((r) => !r.ok && !r.skipped).length;
      setSnackbar({
        severity: failed > 0 ? "warning" : "success",
        message: `Sync all: ${ok} ok${skipped ? `, ${skipped} paused skipped` : ""}${failed ? `, ${failed} failed` : ""}.`,
      });
      await Promise.all([refresh(), refreshLogs()]);
    } catch (e) {
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Sync all failed." });
    } finally {
      setSyncingAll(false);
    }
  }

  async function handleSyncPlatform(provider) {
    const rows = connected.filter((c) => c.provider === provider && !c.syncPaused);
    if (rows.length === 0) {
      setSnackbar({ severity: "warning", message: "No active (non-paused) accounts to sync." });
      return;
    }
    setSyncingPlatform(provider);
    let ok = 0;
    let failed = 0;
    try {
      for (const row of rows) {
        try {
          await InsightsService.refreshOne(row.id);
          ok += 1;
        } catch {
          failed += 1;
        }
      }
      setSnackbar({
        severity: failed > 0 ? "warning" : "success",
        message: `Synced ${ok} account(s)${failed ? `, ${failed} failed` : ""}.`,
      });
      await Promise.all([refresh(), refreshLogs()]);
    } finally {
      setSyncingPlatform(null);
    }
  }

  async function handlePauseToggle(row) {
    setBusyId(row.id);
    try {
      const updated = await IntegrationsService.updateSettings(row.id, { syncPaused: !row.syncPaused });
      setConnected((prev) => prev.map((c) => (c.id === row.id ? { ...c, ...updated } : c)));
      setSnackbar({
        severity: "success",
        message: updated.syncPaused ? "Sync paused — hidden from Report builder." : "Sync resumed.",
      });
    } catch (e) {
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Failed to update pause state." });
    } finally {
      setBusyId(null);
    }
  }

  async function handleSaveSettings(patch) {
    if (!settingsRow) return;
    setSavingSettings(true);
    try {
      const updated = await IntegrationsService.updateSettings(settingsRow.id, patch);
      setConnected((prev) => prev.map((c) => (c.id === settingsRow.id ? { ...c, ...updated } : c)));
      setSettingsRow(null);
      setSnackbar({ severity: "success", message: "Settings saved." });
    } catch (e) {
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Failed to save settings." });
    } finally {
      setSavingSettings(false);
    }
  }

  function openDisconnect(row) {
    setDeleteStoredData(false);
    setConfirmRow(row);
  }

  function openDisconnectAll(provider) {
    const rows = connected.filter((c) => c.provider === provider.provider);
    if (rows.length === 0) return;
    setDeleteStoredData(false);
    setBulkAck(false);
    setConfirmBulk({
      provider: provider.provider,
      label: provider.label,
      rows,
    });
  }

  async function handleDisconnect(row, { deleteData = false } = {}) {
    setBusyId(row.id);
    try {
      await IntegrationsService.disconnect(row.id, { deleteData });
      const name = getAccountDisplayTitle(row);
      setSnackbar({
        severity: "success",
        message: deleteData
          ? `${name} disconnected and its stored data was deleted.`
          : `${name} disconnected. Stored data was kept.`,
      });
      setConfirmRow(null);
      setDeleteStoredData(false);
      await refresh();
    } catch (e) {
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Disconnect failed." });
    } finally {
      setBusyId(null);
    }
  }

  async function handleDisconnectAll() {
    if (!confirmBulk?.rows?.length) return;
    setDisconnectingPlatform(confirmBulk.provider);
    const rows = confirmBulk.rows;
    const deleteData = deleteStoredData;
    let ok = 0;
    let failed = 0;
    try {
      for (const row of rows) {
        try {
          await IntegrationsService.disconnect(row.id, { deleteData });
          ok += 1;
        } catch {
          failed += 1;
        }
      }
      setSnackbar({
        severity: failed > 0 ? "warning" : "success",
        message: `Disconnected ${ok} ${confirmBulk.label} account(s)${failed ? `, ${failed} failed` : ""}${deleteData ? ". Stored data deleted." : ". Stored data kept."}`,
      });
      setConfirmBulk(null);
      setBulkAck(false);
      setDeleteStoredData(false);
      await refresh();
    } finally {
      setDisconnectingPlatform(null);
    }
  }

  async function handleDiscoverCatalogs() {
    setDiscoveringCatalogs(true);
    try {
      const result = await IntegrationsService.discoverMetaCatalogs();
      const count = result?.count ?? 0;
      const removed = Array.isArray(result?.removedNonOwned) ? result.removedNonOwned.length : 0;
      if (result?.ok === false || count === 0) {
        const base = result?.hint || "No owned product catalogs found.";
        setSnackbar({
          severity: "warning",
          message: removed > 0 ? `${base} Removed ${removed} non-owned catalog connection(s).` : base,
        });
        await refresh();
        return;
      }
      setSnackbar({
        severity: "success",
        message: `Connected ${count} owned catalog(s)${removed > 0 ? `; removed ${removed} non-owned` : ""}.`,
      });
      await refresh();
    } catch (e) {
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Catalog discovery failed." });
    } finally {
      setDiscoveringCatalogs(false);
    }
  }

  async function handleSignOut() {
    try { await ApiService.signout(); } catch { /* ignore */ }
    navigate("/signin");
  }

  const hasMetaConnection = connected.some((c) => c.provider === "meta");

  return (
    <AppShell account={account} onSignOut={handleSignOut} title="Data sources">
      <div className="flex w-full flex-col gap-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">Data sources</h2>
            <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-slate-500">
              Connect your Meta, Google, Ads, and Shopify accounts through Orka&apos;s apps. Each merchant connects their own accounts — Orka does not use a shared company Ads login.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSyncAll}
            disabled={syncingAll || connected.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {syncingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {syncingAll ? "Syncing…" : "Sync all"}
          </button>
        </div>

        <div className="flex gap-3 rounded-xl border border-indigo-100 bg-indigo-50/70 px-4 py-3 text-sm text-indigo-950">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
          <div className="space-y-1 leading-relaxed">
            <p>
              <strong className="font-semibold">Data sources</strong> connect &amp; sync your raw accounts.{" "}
              <strong className="font-semibold">Report builder</strong> charts already-synced sources.{" "}
              <strong className="font-semibold">MCP</strong> is read-only over synced data (no live platform calls unless you refresh).
            </p>
          </div>
        </div>

        {/* Overview strip */}
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: "Connected", value: overview.total },
            { label: "Errors", value: overview.errors, accent: overview.errors > 0 ? "text-red-600" : "" },
            { label: "Paused", value: overview.paused, accent: overview.paused > 0 ? "text-amber-700" : "" },
            { label: "Last sync", value: fmtDateTime(overview.lastSync), small: true },
          ].map((card) => (
            <div key={card.label} className="analytics-card px-4 py-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{card.label}</p>
              <p className={cn("mt-1 font-semibold text-slate-900", card.small ? "text-sm" : "text-2xl", card.accent)}>
                {loading ? "—" : card.value}
              </p>
            </div>
          ))}
        </section>

        {error && (
          <div className="flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)}><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* Platform cards */}
        <section>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Platforms</h3>
            <p className="mt-0.5 text-sm text-slate-500">Select a card to filter active connections below.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {loading && providers.length === 0
              ? [0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="analytics-card h-48 animate-pulse bg-slate-100" />)
              : providers.map((p) => (
                  <PlatformCard
                    key={p.provider}
                    provider={p}
                    connectedCount={connected.filter((c) => c.provider === p.provider).length}
                    isConnecting={connectingProvider === p.provider}
                    onConnect={handleConnect}
                    onSyncPlatform={handleSyncPlatform}
                    syncingPlatform={syncingPlatform}
                    onDisconnectAll={openDisconnectAll}
                    disconnectingPlatform={disconnectingPlatform}
                    selected={platformFilter === p.provider}
                    onSelect={(id) => setPlatformFilter((prev) => (prev === id ? "all" : id))}
                  />
                ))}
          </div>
        </section>

        {/* Filter tabs */}
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          {PLATFORM_TABS.map((tab) => {
            const count = tab.providers
              ? connected.filter((c) => tab.providers.includes(c.provider)).length
              : connected.length;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPlatformFilter(tab.id)}
                className={cn(
                  "inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition",
                  platformFilter === tab.id
                    ? "border-indigo-600 bg-indigo-600 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                )}
              >
                {tab.label}
                <span className={cn(
                  "rounded-full px-1.5 py-0.5 text-[10px]",
                  platformFilter === tab.id ? "bg-white/20" : "bg-slate-100 text-slate-500"
                )}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Active connections */}
        <section>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Active connections</h3>
              <p className="mt-0.5 text-sm text-slate-500">
                {platformFilter === "all" ? "All connected accounts" : `Filtered · ${PLATFORM_TABS.find((t) => t.id === platformFilter)?.label}`}
              </p>
            </div>
            {hasMetaConnection && (
              <div className="flex flex-col items-stretch gap-1 sm:items-end">
                <button
                  type="button"
                  onClick={handleDiscoverCatalogs}
                  disabled={discoveringCatalogs}
                  className="inline-flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-900 hover:bg-orange-100 disabled:opacity-60"
                >
                  {discoveringCatalogs ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Package className="h-3.5 w-3.5" />}
                  Discover Meta catalogs
                </button>
                <p className="max-w-xs text-[11px] leading-snug text-slate-500 sm:text-right">
                  Shows catalogs your Business Manager owns. Client/shared catalogs are not connected.
                </p>
              </div>
            )}
          </div>

          {loading && filteredConnected.length === 0 ? (
            <div className="h-24 animate-pulse rounded-xl bg-slate-100" />
          ) : filteredConnected.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
              <p className="text-sm text-slate-500">No connections in this filter. Connect your accounts above.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {(metaGroups || [{ label: null, rows: filteredConnected }]).map((group) => (
              <div key={group.label || "all"} className="space-y-3">
              {group.label ? (
                <h4 className="text-sm font-semibold text-slate-700">{group.label}</h4>
              ) : null}
              {group.rows.map((row) => {
                const isBusy = busyId === row.id;
                return (
                  <div
                    key={row.id}
                    className="flex flex-col gap-4 rounded-xl border border-slate-200/80 bg-white px-4 py-4 sm:flex-row sm:items-center"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      {row.avatarUrl ? (
                        <img src={row.avatarUrl} alt="" className="h-11 w-11 shrink-0 rounded-xl border border-slate-200 object-cover" />
                      ) : (
                        <ProviderBrandIcon provider={row.provider} />
                      )}
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <AccountLabel account={row} nameClassName="font-semibold text-slate-900" />
                          <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                            {kindLabel(row.providerAccountKind)}
                          </span>
                          <AccountIdBadge account={row} />
                          <StatusBadge row={row} />
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          Last synced: {fmtDateTime(row.lastSyncedAt)}
                          {row.lastError ? (
                            <span className="ml-2 text-red-600">· {row.lastError}</span>
                          ) : null}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                      <button
                        type="button"
                        disabled={isBusy || row.syncPaused}
                        onClick={() => handleRefreshOne(row.id)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        title={row.syncPaused ? "Resume sync before syncing" : "Sync now"}
                      >
                        {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                        Sync
                      </button>
                      <button
                        type="button"
                        onClick={() => setSettingsRow(row)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <Settings2 className="h-3.5 w-3.5" />
                        Edit settings
                      </button>
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => handlePauseToggle(row)}
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold disabled:opacity-50",
                          row.syncPaused
                            ? "bg-emerald-600 text-white hover:bg-emerald-700"
                            : "border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
                        )}
                      >
                        {row.syncPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
                        {row.syncPaused ? "Resume sync" : "Pause sync"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setHistoryAccount(row)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
                        title="History"
                      >
                        <History className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => openDisconnect(row)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-100 bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50"
                        title="Disconnect"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
              </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Sync log</h3>
              <p className="mt-0.5 text-sm text-slate-500">Last 20 background sync runs</p>
            </div>
            <button
              type="button"
              onClick={refreshLogs}
              disabled={logsLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {logsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
          </div>
          <div className="analytics-card overflow-hidden">
            <SyncLogTable logs={syncLogs} loading={logsLoading} />
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-4 text-sm">
          <RouterLink to="/reports" className="inline-flex items-center gap-1.5 font-semibold text-indigo-600 hover:text-indigo-800">
            Open Report builder
            <ArrowRight className="h-3.5 w-3.5" />
          </RouterLink>
          <RouterLink to="/" className="inline-flex items-center gap-1.5 font-medium text-slate-500 hover:text-slate-800">
            <ArrowLeft className="h-4 w-4" />
            Back to overview
          </RouterLink>
        </div>
      </div>

      {confirmRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button type="button" aria-label="Close" className="absolute inset-0 bg-slate-900/50" onClick={() => setConfirmRow(null)} />
          <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
            <div className="mb-3 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0 text-red-500" />
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Disconnect account?</h3>
                <p className="mt-2 text-sm text-slate-600">
                  This removes OAuth tokens for <strong>{getAccountDisplayTitle(confirmRow)}</strong> and
                  stops scheduled syncs. The account leaves Overview and this list.
                  Prefer <strong>Pause sync</strong> if you only want to stop pulls.
                </p>
              </div>
            </div>
            <label className="mt-4 flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={deleteStoredData}
                onChange={(e) => setDeleteStoredData(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
              />
              <span>
                <span className="font-medium">Also delete its stored data</span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  Snapshots and metric history. Leave this off to keep historical data.
                </span>
              </span>
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setConfirmRow(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button
                type="button"
                disabled={busyId === confirmRow.id}
                onClick={() => handleDisconnect(confirmRow, { deleteData: deleteStoredData })}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                {busyId === confirmRow.id && <Loader2 className="h-4 w-4 animate-spin" />}
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmBulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => { setConfirmBulk(null); setBulkAck(false); }}
          />
          <div className="relative w-full max-w-md rounded-2xl border border-red-200 bg-white p-6 shadow-xl">
            <div className="mb-3 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Disconnect all {confirmBulk.label} accounts?
                </h3>
                <p className="mt-2 text-sm text-slate-600">
                  This removes OAuth tokens and stops scheduled syncs for{" "}
                  <strong>{confirmBulk.rows.length}</strong> {confirmBulk.label} account
                  {confirmBulk.rows.length === 1 ? "" : "s"}. They will disappear from Overview,
                  platform counts, and report source pickers.
                </p>
                <ul className="mt-3 max-h-28 space-y-1 overflow-y-auto text-sm text-slate-700">
                  {confirmBulk.rows.slice(0, 8).map((row) => (
                    <li key={row.id} className="truncate">· {getAccountDisplayTitle(row)}</li>
                  ))}
                  {confirmBulk.rows.length > 8 && (
                    <li className="text-slate-500">and {confirmBulk.rows.length - 8} more</li>
                  )}
                </ul>
              </div>
            </div>
            <label className="mt-4 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-900">
              <input
                type="checkbox"
                checked={bulkAck}
                onChange={(e) => setBulkAck(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-500"
              />
              <span className="font-medium">
                I understand this disconnects all {confirmBulk.rows.length} {confirmBulk.label} accounts.
              </span>
            </label>
            <label className="mt-3 flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={deleteStoredData}
                onChange={(e) => setDeleteStoredData(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
              />
              <span>
                <span className="font-medium">Also delete their stored data</span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  Off by default. Historical snapshots and metric rows stay unless you check this.
                </span>
              </span>
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setConfirmBulk(null); setBulkAck(false); }}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!bulkAck || disconnectingPlatform === confirmBulk.provider}
                onClick={handleDisconnectAll}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                {disconnectingPlatform === confirmBulk.provider && <Loader2 className="h-4 w-4 animate-spin" />}
                Disconnect all
              </button>
            </div>
          </div>
        </div>
      )}

      <SettingsDrawer
        row={settingsRow}
        open={Boolean(settingsRow)}
        onClose={() => setSettingsRow(null)}
        onSave={handleSaveSettings}
        saving={savingSettings}
      />

      <HistoryDrawer
        connectedAccount={historyAccount}
        open={Boolean(historyAccount)}
        onClose={() => setHistoryAccount(null)}
      />

      <Toast snackbar={snackbar} onClose={() => setSnackbar(null)} />
    </AppShell>
  );
}
